import { defer, distinctUntilChanged, finalize, map, Observable, scan, startWith, Subject } from 'rxjs';
import { Inject, Optional, Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpContextToken } from '@angular/common/http';

import { HTTP_BUSY_STATE_IGNORED_URLS } from './http-busy-state-ignored-urls.provider';

/**
 * Intercepts http calls throughout the app and signals the busy state of the http service.
 * Hook this into the app by providing {@link httpBusyStateProviders} in your app component.
 *
 * Specify urls to ignore and not consider as busy using `provideHttpBusyStateIgnoredUrl`.
 *
 * @export
 * @class HttpBusyStateInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable({ providedIn: 'root' })
export class HttpBusyStateInterceptor implements HttpInterceptor {
    private readonly urlIsBusy$ = new Subject<{ url: string; busy: boolean }>();

    public readonly busy$: Observable<boolean>;

    constructor(@Inject(HTTP_BUSY_STATE_IGNORED_URLS) @Optional() private ignoredUrls?: string[]) {
        this.busy$ = this.createBusyFeed();
    }

    public intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (this.shouldIgnoreRequest(request)) return next.handle(request);

        return this.observeAndHandleRequest(request, next);
    }

    private createBusyFeed(): Observable<boolean> {
        return this.urlIsBusy$.pipe(
            scan(trackActiveRequestsPerUrl, createRequestTracker()),
            map(hasActiveRequests),
            startWith(false),
            distinctUntilChanged()
        );
    }

    private observeAndHandleRequest(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const url = request.url;

        return defer(() => {
            this.urlIsBusy$.next({ url, busy: true });

            return next.handle(request).pipe(finalize(() => this.urlIsBusy$.next({ url, busy: false })));
        });
    }

    private shouldIgnoreRequest({ context, url }: HttpRequest<unknown>): boolean {
        return (
            (!!this.ignoredUrls?.length && this.ignoredUrls.some((ignoredUrl) => url.includes(ignoredUrl))) ||
            context.get(IGNORE_CLIENT_LOADING)
        );
    }
}

export const IGNORE_CLIENT_LOADING = new HttpContextToken(() => false);

function createRequestTracker(): Map<string, number> {
    return new Map<string, number>();
}

/**
 * As http call order and duration are unexpected, concurrency issues might arise. If a request is finished earlier than the one bofore it,
 * it will end-up signaling idle when there's still an active http call in progress. The interceptor tracks the amount
 * of calls in progress made to each url and uses the map to determine wether any requests are still running.
 *
 * @param {Map<string, number>} activeRequests
 * @param {{ url: string; busy: boolean }} { url, busy }
 * @return {*}  {Map<string, number>}
 */
function trackActiveRequestsPerUrl(
    activeRequests: Map<string, number>,
    { url, busy }: { url: string; busy: boolean }
): Map<string, number> {
    let inProgress = activeRequests.get(url) ?? 0;

    busy ? ++inProgress : --inProgress;

    if (inProgress > 0) activeRequests.set(url, inProgress);
    else activeRequests.delete(url);

    return activeRequests;
}

function hasActiveRequests(activeRequests: Map<string, number>): boolean {
    return activeRequests.size > 0;
}
